import React from 'react';
import _get from 'lodash/get';
import classnames from 'classnames';
import * as Types from 'pages/Pages/typings';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';

import makeStyles from '@material-ui/core/styles/makeStyles';

interface Props {
  values: Types.EditSettings;
  onChange: (field: string, value: any) => void;
}

function PageMemberGrouping({ values, onChange }: Props) {
  const classes = useStyles();
  const selectOptions = _get(values, 'relationshipMetadataAttribute.selectOptions', '');
  const groupingEnabled = _get(values, 'relationshipMetadataAttribute.enabled', false);

  const handleAllowMemberGrouping = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('settings.relationshipMetadataAttribute.enabled', e.target.checked);
  };

  const handleGroupMembersByChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('settings.relationshipMetadataAttribute.displayName', e.target.value);
  };

  const handleGroupOptionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('settings.relationshipMetadataAttribute.selectOptions', e.target.value);
  };

  const handleAllowEditorsCreateNewGroups = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('settings.relationshipMetadataAttribute.allowCreation', e.target.checked);
  };

  return (
    <Card classes={{ root: classes.root }}>
      <CardHeader
        title="Page Member Grouping"
        subheader=" If grouping is turned on, when members added to the page, editor will be asked to assign a group to them."
        action={
          <Switch
            checked={groupingEnabled}
            inputProps={{ 'aria-label': 'allow grouping switch' }}
            onChange={handleAllowMemberGrouping}
          />
        }
      />
      <CardContent>
        <div className={classes.info}>
          <Typography variant="caption">
            Page type “Awards” can group members by “Year”, and the options can be “2018”, “2019”.
            Page type “Virtual Team” can group members by “Role”, and the options can be “Lead”,
            “Designer”, “Developer”...
          </Typography>
        </div>
        <div className={classnames(classes.section, { [classes.disabled]: !groupingEnabled })}>
          <Typography variant="h6">Group Members by</Typography>
          <Typography variant="subtitle2">
            This will be the keyword we use to ask editors.
          </Typography>
          <TextField
            id="group-members-by"
            disabled={!groupingEnabled}
            fullWidth={true}
            value={_get(values, 'relationshipMetadataAttribute.displayName', '')}
            onChange={handleGroupMembersByChange}
            inputProps={{ 'aria-label': 'group-members-by', maxLength: 5000 }}
          />
        </div>
        <div className={classnames(classes.section, { [classes.disabled]: !groupingEnabled })}>
          <Typography variant="h6">Group Options</Typography>
          <Typography variant="subtitle2">
            Editors can create groups for individual pages. If you want to provide group options
            that all pages of this type can choose from, you can add here. Separate options by
            commas or line breaks.
          </Typography>
          <TextField
            id="group-options"
            multiline
            disabled={!groupingEnabled}
            fullWidth={true}
            value={selectOptions}
            onChange={handleGroupOptionsChange}
            inputProps={{ 'aria-label': 'group-options', maxLength: 5000 }}
          />
        </div>
        <div className={classnames(classes.content, { [classes.disabled]: !groupingEnabled })}>
          <div>
            <Typography variant="h6">Allow editors to create new groups</Typography>
            <Typography variant="subtitle2">
              If enabled, editors can assign page members to new groups which are not in group
              options.
            </Typography>
          </div>
          <Switch
            className={classes.newGroupsSwitch}
            disabled={!groupingEnabled}
            checked={_get(values, 'relationshipMetadataAttribute.allowCreation', false)}
            inputProps={{ 'aria-label': 'allow new groups switch' }}
            onChange={handleAllowEditorsCreateNewGroups}
          />
        </div>
      </CardContent>
    </Card>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    maxWidth: 900,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  newGroupsSwitch: {
    marginRight: -theme.spacing(),
  },
  info: {
    background: 'rgba(41, 98, 255, 0.04) !important',
    border: `1px solid rgba(41, 98, 255, 0.12)`,
    borderRadius: theme.spacing(0.75),
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0.5, 1),
    width: 'fit-content',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  disabled: {
    opacity: theme.palette.opacity.thirtyEightPercent,
  },
}));

export default PageMemberGrouping;
